/**
 * The element that actually scrolls — see the contract comment on it in App.vue. Exported so a
 * test can assert App.vue still carries the attribute: drop it there and this fix silently dies.
 */
export const SCROLL_ROOT_SELECTOR = "[data-scroll-root]";
/**
 * Offset of the scroll root when each location was last left, so back/forward can put the user
 * where they were. vue-router's own `savedPosition` records `window.scrollY`, always 0 on this
 * layout, so the offset has to be tracked here.
 */
const rememberedOffsets = new Map();
/** Enough for any realistic back-stack; keeps a long session from growing the map forever. */
const MAX_REMEMBERED = 50;
const scrollRoot = () => document.querySelector(SCROLL_ROOT_SELECTOR);
/**
 * Records where the location being left was scrolled to. Called from the router's `beforeEach`,
 * the last point where the outgoing page is still on screen.
 */
export function rememberScrollOffset(fullPath) {
    const root = scrollRoot();
    if (!root) {
        return;
    }
    // Re-inserting makes this the newest entry, so eviction below drops a genuinely stale one.
    rememberedOffsets.delete(fullPath);
    rememberedOffsets.set(fullPath, root.scrollTop);
    if (rememberedOffsets.size > MAX_REMEMBERED) {
        const oldest = rememberedOffsets.keys().next().value;
        if (oldest !== undefined) {
            rememberedOffsets.delete(oldest);
        }
    }
}
/**
 * Scroll strategy for every router navigation.
 *
 * The document never scrolls: `html` and `body` are `h-full overflow-hidden`
 * (shared/styles/main.css), and the content area beside the sidebar is what carries the
 * offset. vue-router's own `{ top: 0 }` goes through `window.scrollTo`, a no-op on this
 * layout — so the offset is moved on that element by hand and the behaviour always returns
 * `false` to tell vue-router there is nothing left for it to do.
 *
 * - A new page (`savedPosition` is null, which vue-router leaves so only on a push) starts at
 *   the top. Without this, a navigation fired from far down a page kept its offset: opening an
 *   organisation fiche from the create-organisation modal — reached from the address block, low
 *   on a contact fiche — landed on a page already scrolled to the bottom, reading as a broken
 *   screen.
 * - Back/forward returns to the recorded offset, so leaving a long list and coming back does
 *   not lose the user's place. With nothing recorded (deep link, restored session) the offset
 *   is left alone rather than forced to the top.
 * - A query- or hash-only change keeps the current offset: those drive tab switches, list
 *   filters and modal-in-URL flows, where jumping would throw away the place in a list still
 *   being read.
 *
 * Restoring is best-effort: a page that renders its rows after an async fetch may still be
 * short when this runs, and the browser clamps the offset to what currently fits.
 */
export const handleRouteScroll = (to, from, savedPosition) => {
    const root = scrollRoot();
    if (!root || to.path === from.path) {
        return false;
    }
    if (savedPosition) {
        const remembered = rememberedOffsets.get(to.fullPath);
        if (remembered !== undefined) {
            root.scrollTo({ top: remembered });
        }
        return false;
    }
    root.scrollTo({ top: 0 });
    return false;
};
